import { createSelector } from "reselect";

import { getMasterRuleSets, getMasterRules, getRuleSets } from "selectors/root";

import {
  selectMasterRuleIdsByMasterRuleBookIdLookup,
  selectMasterRuleSetIdsByMasterRuleBookIdLookup,
  selectRuleSetIdsByRuleBookIdLookup,
} from "selectors/indexes";
import { createLookupCombiner, createLookupSelectors } from "selectors/lib";

export const getRuleSetById = (ruleSetId: string) => (state: RootState) =>
  getRuleSets(state)[ruleSetId] || null;
export const getMasterRuleSetById = (ruleSetId: string) => (state: RootState) =>
  getMasterRuleSets(state)[ruleSetId] || null;

type RuleSetLike = RuleSet | MasterRuleSet;

function ruleSetOptionsReducer<RuleSetVariant extends RuleSetLike>(
  ruleSetVariantByIdLookup: Record<string, RuleSetVariant>,
) {
  return Object.values(ruleSetVariantByIdLookup).map(ruleSet => ({
    label: ruleSet.name || `(Unnamed Rule Set ) ${ruleSet.input_field}`,
    value: ruleSet.id,
  }));
}

export const getRuleSetOptions = createSelector(
  [getRuleSets],
  ruleSetOptionsReducer,
);
export const getMasterRuleSetOptions = createSelector(
  [getMasterRuleSets],
  ruleSetOptionsReducer,
);

export const [
  selectMasterRuleSetsByMasterRuleBookIdLookup,
  getMasterRuleSetsByMasterRuleBookId,
] = createLookupSelectors(
  [selectMasterRuleIdsByMasterRuleBookIdLookup, getMasterRules],
  createLookupCombiner((masterRuleIds, masterRuleByIdLookup) =>
    masterRuleIds.map(masterRuleId => masterRuleByIdLookup[masterRuleId]),
  ),
);

export const [selectRuleSetsByRuleBookIdLookup, getRuleSetsByRuleBookId] =
  createLookupSelectors(
    [selectRuleSetIdsByRuleBookIdLookup, getRuleSets],
    createLookupCombiner((ruleIds, ruleByIdLookup) =>
      ruleIds.map(ruleId => ruleByIdLookup[ruleId]),
    ),
  );

export const [selectRuleSetOptionsByRuleBookId, getRuleSetOptionsByRuleBookId] =
  createLookupSelectors(
    [selectRuleSetIdsByRuleBookIdLookup, getRuleSetOptions],
    createLookupCombiner((ruleSetIds, ruleSetOptions) =>
      ruleSetIds.map(ruleSetId =>
        ruleSetOptions.find(option => option.value === ruleSetId),
      ),
    ),
  );
export const [
  selectMasterRuleSetOptionsByMasterRuleBookId,
  getMasterRuleSetOptionsByMasterRuleBookId,
] = createLookupSelectors(
  [selectMasterRuleSetIdsByMasterRuleBookIdLookup, getMasterRuleSetOptions],
  createLookupCombiner((ruleSetIds, ruleSetOptions) =>
    ruleSetIds.map(ruleSetId =>
      ruleSetOptions.find(option => option.value === ruleSetId),
    ),
  ),
);
